/**
 * Updates the recently viewed products in localStorage.
 *
 * The list is a convenience feature for the search preview, so it is not strictly
 * necessary. Storing and reading it is therefore gated on the visitor's consent for
 * the "preferences" purpose, read through Shopify's Customer Privacy API.
 *
 * @see https://shopify.dev/docs/api/customer-privacy
 */

/** @constant {string} The key used to store the viewed products in local storage */
const STORAGE_KEY = 'viewedProducts';
/** @constant {number} The maximum number of products to store */
const MAX_PRODUCTS = 4;
/** @constant {object} The feature that exposes window.Shopify.customerPrivacy */
const CONSENT_FEATURE = { name: 'consent-tracking-api', version: '0.1' };
/** @constant {string} The document event the Customer Privacy API publishes when consent changes */
const CONSENT_EVENT = 'visitorConsentCollected';
/** @constant {number} How long to wait for window.Shopify to show up, in milliseconds */
const SHOPIFY_TIMEOUT = 3000;
/** @constant {number} How often to look for window.Shopify, in milliseconds */
const SHOPIFY_POLL_INTERVAL = 100;
/** @constant {number} How long to wait for loadFeatures to call back, in milliseconds */
const LOAD_FEATURES_TIMEOUT = 5000;

/**
 * Consent for the "preferences" purpose. `null` means unknown, which is treated like
 * a refusal for writing and reading, but does not remove anything either.
 * @type {boolean | null}
 */
let consentGranted = null;

/** @type {Promise<void> | null} */
let consentLookup = null;

/**
 * Reads the current consent from the Customer Privacy API.
 * @returns {boolean | null} `null` when the API is unavailable or fails.
 */
function readConsent() {
  try {
    const api = globalThis.Shopify?.customerPrivacy;

    if (!api || typeof api.preferencesProcessingAllowed !== 'function') return null;

    return api.preferencesProcessingAllowed() === true;
  } catch {
    return null;
  }
}

/**
 * Removes the stored products without touching the consent state.
 */
function removeStoredProducts() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Local storage can be unavailable, there is nothing to clean up then.
  }
}

/**
 * Reads the stored products without checking consent.
 * @returns {string[]} The stored product ids.
 */
function readStoredProducts() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');

    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
}

/**
 * Applies a consent decision and cleans up when consent is refused or withdrawn.
 * @param {boolean | null} granted - The new consent state.
 */
function applyConsent(granted) {
  consentGranted = granted;

  if (granted === false) removeStoredProducts();
}

/**
 * Waits until window.Shopify is available.
 * @returns {Promise<any | null>} The Shopify object, or `null` after the timeout.
 */
function waitForShopify() {
  return new Promise((resolve) => {
    const deadline = Date.now() + SHOPIFY_TIMEOUT;

    const check = () => {
      if (globalThis.Shopify) return resolve(globalThis.Shopify);
      if (Date.now() >= deadline) return resolve(null);

      setTimeout(check, SHOPIFY_POLL_INTERVAL);
    };

    check();
  });
}

/**
 * Loads the Customer Privacy API. Resolves in every case, errors are left to readConsent.
 * @returns {Promise<void>}
 */
function loadConsentApi() {
  return waitForShopify().then(
    (shopify) =>
      new Promise((resolve) => {
        if (!shopify || shopify.customerPrivacy || typeof shopify.loadFeatures !== 'function') {
          return resolve();
        }

        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve();
        };
        const timer = setTimeout(done, LOAD_FEATURES_TIMEOUT);

        try {
          shopify.loadFeatures([CONSENT_FEATURE], done);
        } catch {
          done();
        }
      })
  );
}

/**
 * Determines the consent state once per page load.
 * @returns {Promise<void>} Resolves when the state is as good as it gets.
 */
function resolveConsent() {
  consentLookup ??= loadConsentApi().then(() => {
    const granted = readConsent();

    if (granted !== null) applyConsent(granted);
  });

  return consentLookup;
}

/**
 * Reacts to a consent change published by the Customer Privacy API.
 * @param {Event} event - The visitorConsentCollected event.
 */
function handleConsentCollected(event) {
  const detail = /** @type {CustomEvent} */ (event).detail;
  const granted = detail?.preferencesAllowed;

  applyConsent(typeof granted === 'boolean' ? granted : readConsent());
}

if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
  document.addEventListener(CONSENT_EVENT, handleConsentCollected);
}

export class RecentlyViewed {
  /**
   * Adds a product to the recently viewed products list, if the visitor consented.
   * @param {string} productId - The ID of the product to add.
   * @returns {Promise<void>} Resolves once the decision has been made.
   */
  static addProduct(productId) {
    return resolveConsent().then(() => {
      if (consentGranted !== true) return;

      let viewedProducts = readStoredProducts();

      viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
      viewedProducts.unshift(productId);
      viewedProducts = viewedProducts.slice(0, MAX_PRODUCTS);

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(viewedProducts));
      } catch {
        // Local storage can be unavailable, the list is optional.
      }
    });
  }

  static clearProducts() {
    removeStoredProducts();
  }

  /**
   * Retrieves the list of recently viewed products, if the visitor consented.
   * @returns {string[]} The list of viewed products, empty without consent.
   */
  static getProducts() {
    void resolveConsent();

    return consentGranted === true ? readStoredProducts() : [];
  }
}
